import { motion } from "framer-motion";
import { AlertTriangle, Check } from "lucide-react";
import { Reveal, Eyebrow, SectionTitle } from "../Reveal";

const STOCK = [
  { label: "WAGO 221", qty: 38, level: 45, low: false },
  { label: "VIS 4×48", qty: 124, level: 90, low: false },
  { label: "CHEVILLES 6 MM", qty: 27, level: 28, low: true },
  { label: "RACCORD 1/2\"", qty: 8, level: 10, low: true },
];

const APP_FEATURES = [
  "Visualisation du stock en un coup d'œil",
  "Suivi des quantités estimées",
  "Alertes de stock faible",
  "Historique de consommation",
  "Gestion de plusieurs boîtes",
  "Association à un véhicule",
];

export default function AppMockup() {
  return (
    <section id="application" className="py-16 sm:py-24" data-testid="app-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
        <Reveal className="order-2 lg:order-1">
          <div className="mx-auto w-full max-w-[320px]" data-testid="app-mockup">
            <div className="border-2 border-[#2E333D] rounded-[2.2rem] bg-[#0C0D0E] p-3 shadow-2xl shadow-black/70">
              <div className="rounded-[1.7rem] bg-[#141619] overflow-hidden">
                <div className="px-5 pt-6 pb-4 border-b border-[#2E333D]">
                  <p className="font-display font-bold uppercase tracking-wider text-[#FFC700] text-lg">Stockbox 8</p>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mt-0.5">Mon stock — véhicule 1</p>
                </div>
                <div className="p-5 space-y-5">
                  {STOCK.map((s, i) => (
                    <div key={s.label} data-testid={`app-stock-${i}`}>
                      <div className="flex items-end justify-between mb-1">
                        <span className="font-mono text-xs text-gray-300">{s.label}</span>
                        <motion.span
                          initial={{ opacity: 0, y: 6 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.5, delay: 0.3 + i * 0.15 }}
                          className={`font-display text-2xl font-bold leading-none ${s.low ? "text-red-400" : "text-[#FFC700]"}`}
                          data-testid={`app-stock-qty-${i}`}
                        >
                          ≈ {s.qty} <span className="text-sm font-semibold">pièces</span>
                        </motion.span>
                      </div>
                      <p className="font-mono text-[9px] uppercase tracking-widest text-gray-600 mb-1.5">Quantité estimée</p>
                      <div className="h-1 rounded bg-[#0C0D0E] overflow-hidden">
                        <motion.div
                          initial={{ width: 0 }}
                          whileInView={{ width: `${s.level}%` }}
                          viewport={{ once: true }}
                          transition={{ duration: 1, delay: 0.3 + i * 0.15, ease: "easeOut" }}
                          className={`h-full ${s.low ? "bg-red-500" : "bg-[#FFC700]/70"}`}
                        />
                      </div>
                      {s.low && (
                        <p className="mt-1.5 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-red-400" data-testid={`app-stock-low-alert-${i}`}>
                          <AlertTriangle size={11} /> Stock faible
                        </p>
                      )}
                    </div>
                  ))}
                </div>
                <div className="px-5 pb-6">
                  <div className="rounded-lg bg-[#0C0D0E] border border-[#2E333D] px-4 py-3 text-center">
                    <span className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Quantités estimées — jamais exactes</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <p className="mt-5 text-center font-mono text-[10px] uppercase tracking-widest text-gray-600 max-w-[320px] mx-auto">
            Interface fictive d'illustration — l'application n'est pas encore disponible
          </p>
        </Reveal>
        <Reveal className="order-1 lg:order-2" delay={0.1}>
          <Eyebrow>Application mobile prévue</Eyebrow>
          <SectionTitle>Votre stock, dans votre poche.</SectionTitle>
          <p className="mt-6 text-gray-400 leading-relaxed max-w-lg">
            L'application mobile prévue permettra de suivre les quantités estimées de pièces
            dans chaque compartiment, de recevoir des alertes et de gérer plusieurs boîtes
            associées à vos véhicules.
          </p>
          <ul className="mt-8 space-y-3">
            {APP_FEATURES.map((f) => (
              <li key={f} className="flex items-start gap-3 text-sm text-gray-300" data-testid={`app-feature-${f.slice(0, 14).replace(/\s/g, "-")}`}>
                <Check size={16} className="text-[#FFC700] mt-0.5 shrink-0" /> {f}
              </li>
            ))}
          </ul>
        </Reveal>
      </div>
    </section>
  );
}
